/**
 * 任务表：16 步教程任务链（逐步解锁玩法）+ 12 个每日任务池 + 20 个阶梯成就。
 */
import type { AchievementDef, QuestDef } from '../core/types';

// ---------------------------------------------------------------- 教程链

export const TUTORIAL_QUESTS: QuestDef[] = [
  {
    id: 'q_welcome',
    name: { zh: '初来乍到', en: 'Fresh Off the Boat' },
    desc: { zh: '在农田里种下 2 颗胡萝卜种子，开始你的岛主生活。', en: 'Plant 2 carrot seeds to begin island life.' },
    steps: [{ type: 'plant', target: 'crop_carrot', count: 2 }],
    reward: { coins: 50, xp: 10 },
  },
  {
    id: 'q_water',
    name: { zh: '雨露均沾', en: 'A Little Water' },
    desc: { zh: '给作物浇 2 次水，浇水能加快生长。', en: 'Water crops twice to speed them up.' },
    steps: [{ type: 'water', count: 2 }],
    reward: { coins: 40, xp: 8, energy: 5 },
    after: 'q_welcome',
  },
  {
    id: 'q_harvest',
    name: { zh: '第一桶金', en: 'First Harvest' },
    desc: { zh: '收获 2 次胡萝卜。', en: 'Harvest carrots 2 times.' },
    steps: [{ type: 'harvest', target: 'crop_carrot', count: 2 }],
    reward: { coins: 80, xp: 15 },
    after: 'q_water',
  },
  {
    id: 'q_sell',
    name: { zh: '小本生意', en: 'Small Business' },
    desc: { zh: '在商店出售 3 件物品换取金币。', en: 'Sell 3 items at the shop.' },
    steps: [{ type: 'sell', count: 3 }],
    reward: { coins: 60, xp: 12 },
    after: 'q_harvest',
  },
  {
    id: 'q_till',
    name: { zh: '开疆拓土', en: 'Break New Ground' },
    desc: { zh: '开垦 2 块新农田。', en: 'Till 2 new plots.' },
    steps: [{ type: 'tillPlot', count: 2 }],
    reward: { coins: 100, xp: 20, items: { seed_potato: 4 } },
    after: 'q_sell',
  },
  {
    id: 'q_weed',
    name: { zh: '岛容岛貌', en: 'Tidy Isle' },
    desc: { zh: '清理岛上的 2 株杂草。', en: 'Clear 2 weeds.' },
    steps: [{ type: 'clearWeed', count: 2 }],
    reward: { coins: 60, xp: 10, energy: 5 },
    after: 'q_till',
  },
  {
    id: 'q_animal',
    name: { zh: '咯咯哒', en: 'Cluck Cluck' },
    desc: { zh: '买 1 只小母鸡，喂食并收取 1 次鸡蛋。', en: 'Buy a hen, feed her, and collect an egg.' },
    steps: [
      { type: 'feedAnimal', target: 'animal_chicken', count: 1 },
      { type: 'collectAnimal', target: 'animal_chicken', count: 1 },
    ],
    reward: { coins: 120, xp: 25, food: 10 },
    after: 'q_weed',
    minLevel: 2,
  },
  {
    id: 'q_fish',
    name: { zh: '愿者上钩', en: 'Gone Fishing' },
    desc: { zh: '在岸边钓上 2 条鱼。', en: 'Catch 2 fish at the shore.' },
    steps: [{ type: 'fishCatch', count: 2 }],
    reward: { coins: 150, xp: 30 },
    after: 'q_animal',
  },
  {
    id: 'q_decor',
    name: { zh: '装点家园', en: 'Home Sweet Home' },
    desc: { zh: '摆放 3 件建筑或装饰。', en: 'Place 3 buildings or decorations.' },
    steps: [{ type: 'place', count: 3 }],
    reward: { coins: 200, xp: 40 },
    after: 'q_fish',
    minLevel: 3,
  },
  {
    id: 'q_beast',
    name: { zh: '守岛卫士', en: 'Isle Defender' },
    desc: { zh: '击退 1 只入侵的野兽。', en: 'Drive off 1 invading beast.' },
    steps: [{ type: 'beastKill', count: 1 }],
    reward: { coins: 250, xp: 50, energy: 10 },
    after: 'q_decor',
  },
  {
    id: 'q_dress',
    name: { zh: '焕然一新', en: 'Fresh Look' },
    desc: { zh: '买 1 件新服装并换上任意装扮。', en: 'Buy a clothing item and change your outfit.' },
    steps: [
      { type: 'buyClothing', count: 1 },
      { type: 'wear', count: 1 },
    ],
    reward: { coins: 150, xp: 30 },
    after: 'q_beast',
    minLevel: 4,
  },
  {
    id: 'q_visit',
    name: { zh: '串门礼仪', en: 'Neighborly Visit' },
    desc: { zh: '拜访 1 位邻居的小岛。', en: 'Visit a neighbor’s island.' },
    steps: [{ type: 'visit', count: 1 }],
    reward: { coins: 100, xp: 25 },
    after: 'q_dress',
  },
  {
    id: 'q_help',
    name: { zh: '举手之劳', en: 'Helping Hand' },
    desc: { zh: '在邻居岛帮工 3 次（浇水/除草/驱兽）。', en: 'Help a neighbor 3 times (water/weed/chase).' },
    steps: [{ type: 'helpNeighbor', count: 3 }],
    reward: { coins: 200, xp: 40, reputation: 5 },
    after: 'q_visit',
  },
  {
    id: 'q_prank',
    name: { zh: '无伤大雅', en: 'Harmless Fun' },
    desc: { zh: '去邻居岛捣蛋 1 次——声望的另一种赚法。', en: 'Prank a neighbor once—the other road to fame.' },
    steps: [{ type: 'prank', count: 1 }],
    reward: { coins: 120, xp: 25, reputation: 5 },
    after: 'q_help',
  },
  {
    id: 'q_pet',
    name: { zh: '毛茸茸伙伴', en: 'Furry Friend' },
    desc: { zh: '领养 1 只宠物并和它玩耍。', en: 'Adopt a pet and play with it.' },
    steps: [
      { type: 'adoptPet', count: 1 },
      { type: 'playPet', count: 1 },
    ],
    reward: { coins: 300, xp: 60, food: 20 },
    after: 'q_prank',
    minLevel: 5,
  },
  {
    id: 'q_expand',
    name: { zh: '更大的世界', en: 'A Bigger World' },
    desc: { zh: '完成第一次扩岛，达到 6 级并攒够 2000 金币。', en: 'Expand your island for the first time.' },
    steps: [{ type: 'expand', count: 1 }],
    reward: { coins: 500, xp: 120, items: { seed_pineapple: 3 } },
    after: 'q_pet',
    minLevel: 6,
  },
];

// ---------------------------------------------------------------- 每日任务池

export const DAILY_QUESTS: QuestDef[] = [
  {
    id: 'dq_harvest10', daily: true,
    name: { zh: '今日丰收', en: 'Daily Harvest' },
    desc: { zh: '收获 10 次作物。', en: 'Harvest crops 10 times.' },
    steps: [{ type: 'harvest', count: 10 }],
    reward: { coins: 200, xp: 40 },
  },
  {
    id: 'dq_water8', daily: true,
    name: { zh: '浇灌达人', en: 'Watering Pro' },
    desc: { zh: '浇水 8 次。', en: 'Water 8 times.' },
    steps: [{ type: 'water', count: 8 }],
    reward: { coins: 150, xp: 30, energy: 8 },
  },
  {
    id: 'dq_fish3', daily: true,
    name: { zh: '出海三网', en: 'Three Casts' },
    desc: { zh: '钓上 3 条鱼。', en: 'Catch 3 fish.' },
    steps: [{ type: 'fishCatch', count: 3 }],
    reward: { coins: 180, xp: 35 },
  },
  {
    id: 'dq_feed5', daily: true,
    name: { zh: '开饭啦', en: 'Feeding Time' },
    desc: { zh: '喂食动物 5 次。', en: 'Feed animals 5 times.' },
    steps: [{ type: 'feedAnimal', count: 5 }],
    reward: { coins: 160, xp: 32, food: 15 },
    minLevel: 3,
  },
  {
    id: 'dq_help5', daily: true,
    name: { zh: '热心岛民', en: 'Kind Islander' },
    desc: { zh: '帮邻居干 5 次活。', en: 'Help neighbors 5 times.' },
    steps: [{ type: 'helpNeighbor', count: 5 }],
    reward: { coins: 220, xp: 45, reputation: 8 },
    minLevel: 4,
  },
  {
    id: 'dq_sell200', daily: true,
    name: { zh: '日进斗金', en: 'Good Business' },
    desc: { zh: '累计赚取 500 金币。', en: 'Earn 500 coins.' },
    steps: [{ type: 'earnCoins', count: 500 }],
    reward: { coins: 250, xp: 50 },
    minLevel: 5,
  },
  {
    id: 'dq_beast2', daily: true,
    name: { zh: '巡岛安保', en: 'Isle Patrol' },
    desc: { zh: '击退 2 只野兽。', en: 'Defeat 2 beasts.' },
    steps: [{ type: 'beastKill', count: 2 }],
    reward: { coins: 260, xp: 55, energy: 10 },
    minLevel: 5,
  },
  {
    id: 'dq_weed4', daily: true,
    name: { zh: '除草日课', en: 'Weeding Duty' },
    desc: { zh: '清理 4 株杂草（自家或邻居家）。', en: 'Clear 4 weeds (yours or neighbors’).' },
    steps: [{ type: 'clearWeed', count: 4 }],
    reward: { coins: 140, xp: 28 },
  },
  {
    id: 'dq_plant12', daily: true,
    name: { zh: '播种时节', en: 'Sowing Season' },
    desc: { zh: '播种 12 次。', en: 'Plant 12 seeds.' },
    steps: [{ type: 'plant', count: 12 }],
    reward: { coins: 200, xp: 42 },
    minLevel: 3,
  },
  {
    id: 'dq_prank2', daily: true,
    name: { zh: '恶作剧之心', en: 'Prankster Heart' },
    desc: { zh: '捣蛋 2 次。', en: 'Pull 2 pranks.' },
    steps: [{ type: 'prank', count: 2 }],
    reward: { coins: 180, xp: 36, reputation: 6 },
    minLevel: 6,
  },
  {
    id: 'dq_pet3', daily: true,
    name: { zh: '遛宠时间', en: 'Pet Time' },
    desc: { zh: '喂宠物或陪宠物玩合计 3 次。', en: 'Feed or play with pets 3 times total.' },
    steps: [{ type: 'playPet', count: 1 }, { type: 'feedPet', count: 1 }],
    reward: { coins: 170, xp: 34, food: 10 },
    minLevel: 7,
  },
  {
    id: 'dq_collect6', daily: true,
    name: { zh: '牧场巡视', en: 'Ranch Rounds' },
    desc: { zh: '收取 6 次动物产出。', en: 'Collect animal goods 6 times.' },
    steps: [{ type: 'collectAnimal', count: 6 }],
    reward: { coins: 240, xp: 48 },
    minLevel: 6,
  },
];

// ---------------------------------------------------------------- 成就

function ach(
  id: string, zh: string, en: string, descZh: string, descEn: string,
  stat: string, tiers: number[], reward: AchievementDef['rewardPerTier'],
): AchievementDef {
  return { id: `ach_${id}`, name: { zh, en }, desc: { zh: descZh, en: descEn }, stat, tiers, rewardPerTier: reward };
}

export const ACHIEVEMENTS: AchievementDef[] = [
  ach('harvest', '收获大师', 'Harvest Master', '累计收获作物', 'Total harvests',
    'harvests', [10, 50, 200, 800, 3000], { coins: 300, xp: 60 }),
  ach('plant', '播种者', 'Sower', '累计播种', 'Total plantings',
    'plantings', [10, 50, 200, 800, 3000], { coins: 250, xp: 50 }),
  ach('water', '灌溉工程师', 'Irrigation Engineer', '累计浇水', 'Total waterings',
    'waterings', [20, 100, 400, 1500], { coins: 250, xp: 50 }),
  ach('fish', '钓鱼佬', 'Angler', '累计钓鱼', 'Total fish caught',
    'fishCaught', [5, 25, 100, 400, 1500], { coins: 300, xp: 60 }),
  ach('rarefish', '深海传说', 'Deep Sea Legend', '钓到稀有及以上的鱼', 'Rare+ fish caught',
    'rareFishCaught', [1, 5, 20, 80], { coins: 800, xp: 150 }),
  ach('beast', '兽群克星', 'Beast Bane', '累计击退野兽', 'Beasts defeated',
    'beastsDefeated', [3, 15, 60, 250], { coins: 400, xp: 80 }),
  ach('ranch', '牧场主', 'Rancher', '累计收取动物产出', 'Animal goods collected',
    'animalsCollected', [10, 50, 200, 800], { coins: 350, xp: 70 }),
  ach('builder', '岛屿设计师', 'Isle Designer', '累计摆放建筑装饰', 'Buildings placed',
    'buildingsPlaced', [5, 20, 60, 150], { coins: 400, xp: 80 }),
  ach('fashion', '时尚岛主', 'Isle Fashionista', '累计购买服装', 'Clothes bought',
    'clothesBought', [3, 10, 25], { coins: 500, xp: 100 }),
  ach('social', '社交花蝴蝶', 'Social Butterfly', '累计拜访邻居', 'Neighbor visits',
    'visits', [5, 25, 100, 400], { coins: 300, xp: 60, reputation: 10 }),
  ach('helper', '岛际活雷锋', 'Island Samaritan', '累计帮工次数', 'Help actions',
    'helps', [10, 50, 200, 800], { coins: 400, xp: 80, reputation: 15 }),
  ach('prank', '捣蛋鬼', 'Trickster', '累计捣蛋次数', 'Pranks pulled',
    'pranks', [3, 15, 60], { coins: 350, xp: 70, reputation: 15 }),
  ach('weed', '除草机器', 'Weed Whacker', '累计除草', 'Weeds cleared',
    'weedsCleared', [10, 50, 200, 800], { coins: 250, xp: 50 }),
  ach('rich', '第一桶金山', 'Coin Mountain', '累计赚取金币', 'Coins earned',
    'coinsEarned', [1000, 10000, 100000, 1000000], { coins: 500, xp: 100 }),
  ach('seller', '贸易大亨', 'Trade Tycoon', '累计出售物品', 'Items sold',
    'itemsSold', [20, 100, 500, 2000], { coins: 400, xp: 80, reputation: 10 }),
  ach('petlover', '宠物知己', 'Pet Whisperer', '累计喂养+玩耍宠物', 'Pet feeds',
    'petFeeds', [5, 25, 100], { coins: 300, xp: 60, food: 20 }),
  ach('quest', '任务狂人', 'Quest Fiend', '累计完成任务', 'Quests completed',
    'questsDone', [5, 20, 60, 150], { coins: 500, xp: 100 }),
  ach('expand', '开拓者', 'Pioneer', '累计扩岛次数', 'Island expansions',
    'expansions', [1, 2, 3, 4], { coins: 1000, xp: 200 }),
  ach('hire', '包工头', 'Foreman', '累计雇佣邻居', 'Neighbors hired',
    'hires', [2, 10, 40], { coins: 350, xp: 70 }),
  ach('daysplayed', '常驻岛民', 'True Islander', '累计游玩天数', 'Days played',
    'daysPlayed', [2, 7, 30, 100], { coins: 600, xp: 120, energy: 20 }),
];
